<template>
  <validation-observer ref="observer" v-slot="{ invalid }">
    <form @submit.prevent="submit">
      <v-container>
        <v-row>
          <v-col v-if="nameDisplayed" cols="12" md="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              name="Jméno (stačí křestní)"
              :rules="nameValidationRules"
            >
              <v-text-field
                v-model="subjectProfileLocal.name"
                :error-messages="errors"
                :label="nameLabel"
                dense
              >
              </v-text-field>
            </validation-provider>
          </v-col>

          <v-col v-if="ageDisplayed" cols="12" md="3" sm="4">
            <validation-provider
              v-slot="{ errors }"
              name="Věk"
              :rules="ageValidationRules"
            >
              <v-text-field
                v-model="subjectProfileLocal.age"
                :error-messages="errors"
                :label="ageLabel"
                dense
              ></v-text-field>
            </validation-provider>
          </v-col>

          <v-col v-if="sexDisplayed" cols="12" md="3" sm="4">
            <validation-provider
              v-slot="{ errors }"
              name="Pohlaví"
              :rules="sexValidationRules"
            >
              <v-select
                v-model="subjectProfileLocal.gender"
                :items="sexItems"
                item-text="label"
                item-value="code"
                :error-messages="errors"
                :label="sexLabel"
                dense
              ></v-select>
            </validation-provider>
          </v-col>

          <v-col v-if="heightDisplayed" cols="12" md="3" sm="4">
            <validation-provider
              v-slot="{ errors }"
              name="Výška"
              :rules="heightValidationRules"
            >
              <v-text-field
                v-model="subjectProfileLocal.height"
                :error-messages="errors"
                :label="heightLabel"
                dense
              ></v-text-field>
            </validation-provider>
          </v-col>

          <v-col v-if="weightDisplayed" cols="12" md="3" sm="4">
            <validation-provider
              v-slot="{ errors }"
              name="Váha"
              :rules="weightValidationRules"
            >
              <v-text-field
                v-model="subjectProfileLocal.weight"
                :error-messages="errors"
                :label="weightLabel"
                dense
              ></v-text-field>
            </validation-provider>
          </v-col>

          <v-col v-if="shoeSizeDisplayed" cols="12" md="6" sm="6">
            <validation-provider
              v-slot="{ errors }"
              name="Velikost boty"
              :rules="shoeSizeValidationRules"
            >
              <v-select
                v-model="subjectProfileLocal.shoeSize"
                :items="shoeSizeItems"
                :error-messages="errors"
                :label="shoeSizeLabel"
                dense
              ></v-select>
            </validation-provider>
          </v-col>

          <v-col v-if="rentAbilityLevelDisplayed" cols="12" md="6" sm="6">
            <validation-provider
              v-slot="{ errors }"
              name="Uroven"
              :rules="rentAbilityLevelValidationRules"
            >
              <v-select
                v-model="subjectProfileLocal.rentAbilityLevel"
                :items="rentAbilityLevelItems"
                item-text="label"
                item-value="code"
                :error-messages="errors"
                :label="rentAbilityLevelLabel"
                dense
              ></v-select>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="8" sm="6">
            <v-btn
              outlined
              class="mr-4"
              color="success"
              type="button"
              :disabled="invalid"
              @click="doSubmit"
            >
              Uložit
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </form>
  </validation-observer>
</template>

<script>
import { between, required } from "vee-validate/dist/rules";
import {
  extend,
  ValidationObserver,
  ValidationProvider,
  setInteractionMode
} from "vee-validate";
import RentProductRequirmentUtil from "@/libs/RentProductRequirmentUtil";

setInteractionMode("eager");

extend("between", {
  ...between,
  message: "{_field_} musí být celé číslo mezi {min} a {max}."
});

extend("required", {
  ...required,
  message: "{_field_} je povinná položka"
});

export default {
  name: "SubjectProfileForm",

  props: {
    subjectProfile: {
      type: Object,
      required: true
    },
    rentProductTypes: {
      type: Array,
      required: true
    }
  },

  components: {
    ValidationProvider,
    ValidationObserver
  },

  data: () => ({
    valid: false,

    sexItems: [
      { code: "MALE", label: "Muž" },
      { code: "FEMALE", label: "Žena" }
    ],

    rentAbilityLevelItems: [
      { code: "BEGINNER", label: "Začátečník" }, // TODO: prohnat prekladem
      { code: "INTERMIDIATE", label: "Středně pokročilý" },
      { code: "EXPERIENCED", label: "Pokročilý" }
    ],
    shoeSizeItems: [
      "EU 18 / 110 mm / UK 2 (D)",
      "EU 18,5 / 115 mm",
      "EU 19 / 120 mm / UK 3 (D)",
      "EU 20 / 125 / UK 4 (D)",
      "EU 20,5 / 130 mm",
      "EU 21 / 135 mm / UK 4,5 (D)",
      "EU 22 / 140 mm / UK 5 (D)",
      "EU 23 / 145 mm / UK 6 (D)",
      "EU 24 / 150 mm / UK 7 (D)",
      "EU 25 / 155 mm / UK 7,5 (D)",
      "EU 25,5 / 160 mm",
      "EU 26 / 165 mm",
      "EU 26,5 / 165 mm / UK 8,5 (D)",
      "EU 27 / 170 mm / UK 9 (D)",
      "EU 27,5 / 170 mm",
      "EU 28 / 175 mm / UK 10 (D)",
      "EU 28,5 / 180 mm",
      "EU 29 / 185 mm / UK 11 (D)",
      "EU 30 / 190 mm",
      "EU 30,5 / 195 mm / UK 12 (D)",
      "EU 31 / 195 mm",
      "EU 31,5 / 200 mm",
      "EU 32 / 205 mm / UK 0",
      "EU 32,5 / 205 mm",
      "EU 33 / 210 mm / UK 1",
      "EU 33,5 / 215 mm",
      "EU 34 / 215 mm / UK 2",
      "EU 34,5 / 220 mm / UK 2",
      "EU 35 / 220-225 mm / UK 2,5",
      "EU 35,5 / 225 mm / UK 3",
      "EU 36 / 230 mm / UK 3,5",
      "EU 36,5 / 235 mm / UK 4",
      "EU 37 / 235-240 mm / UK 4",
      "EU 37,5 / 240 mm / UK 4,5",
      "EU 38 / 245 mm / UK 5",
      "EU 38,5 / 245-250 mm / UK 5,5",
      "EU 39 / 250 mm / UK 5,5",
      "EU 39,5 / 255 mm / UK 6",
      "EU 40 / 255-260 mm / UK 6,5",
      "EU 40,5 / 260 mm / UK 7",
      "EU 41 / 265 mm / UK 7,5",
      "EU 41,5 / 265-270 mm / UK 7,5",
      "EU 42 / 270 mm / UK 8",
      "EU 42,5 / 270-275 mm / UK 8,5",
      "EU 43 / 275 mm / UK 9",
      "EU 43,5 / 280 mm / UK 9,5",
      "EU 44 / 280-285 mm / UK 9,5",
      "EU 44,5 / 285 mm / UK 10",
      "EU 45 / 290 mm / UK 10,5",
      "EU 45,5 / 295 mm / UK 11",
      "EU 46 / 295 mm / UK 11",
      "EU 46,5 / 300 mm / UK 11,5",
      "EU 47 / 305 mm / UK 12",
      "EU 47,5 / 305 mm / UK 12,5",
      "EU 48 / 310 mm / UK 13",
      "EU 48,5 / 310 mm / UK 13,5",
      "EU 49 / 315 mm / UK 14",
      "EU 49,5 / 320 mm / UK 14,5",
      "EU 50 / 325 mm / UK 15",
      "EU 50,5 / 325 mm",
      "EU 51 / 330 mm / UK 15,5",
      "EU 51,5 / 335 mm",
      "EU 52 / 335 mm / UK 16",
      "EU 52,5 / 340 mm",
      "EU 53 / 345 mm"
    ]
  }),

  computed: {
    subjectProfileLocal() {
      return { ...this.subjectProfile };
    },

    rentProductRequirements() {
      return this.$store.state.rentProductRequirements;
    },

    rentProductRequirmentUtil() {
      console.log("rentProductRequirmentUtil:::::");
      console.log(this.rentProductRequirements);
      console.log(this.rentProductTypes);
      let r = new RentProductRequirmentUtil(
        this.rentProductRequirements,
        this.rentProductTypes
      );
      console.log(r);
      return r;
    },

    nameLabel() {
      let str = "Jméno";
      str += this.rentProductRequirmentUtil.isNameRequired() ? "*" : "";
      return str;
    },

    sexLabel() {
      let str = "Pohlaví";
      str += this.rentProductRequirmentUtil.isSexRequired() ? "*" : "";
      return str;
    },

    ageLabel() {
      let str = "Věk";
      str += this.rentProductRequirmentUtil.isAgeRequired() ? "*" : "";
      return str;
    },

    heightLabel() {
      let str = "Výška (cm)";
      str += this.rentProductRequirmentUtil.isHeightRequired() ? "*" : "";
      return str;
    },

    weightLabel() {
      let str = "Váha (kg)";
      str += this.rentProductRequirmentUtil.isWeightRequired() ? "*" : "";
      return str;
    },

    shoeSizeLabel() {
      let str = "Velikost boty";
      str += this.rentProductRequirmentUtil.isShoeSizeRequired() ? "*" : "";
      return str;
    },

    rentAbilityLevelLabel() {
      let str = "Úroveň";
      str += this.rentProductRequirmentUtil.isRentAbilityLevelRequired()
        ? "*"
        : "";
      return str;
    },

    nameValidationRules() {
      return this.rentProductRequirmentUtil.isNameRequired() ? "required" : "";
    },

    ageValidationRules() {
      return this.rentProductRequirmentUtil.isAgeRequired()
        ? "required|between:3,120"
        : "between:3,120";
    },

    sexValidationRules() {
      return this.rentProductRequirmentUtil.isSexRequired() ? "required" : "";
    },

    heightValidationRules() {
      return this.rentProductRequirmentUtil.isHeightRequired()
        ? "required|between:50,300"
        : "between:50,300";
    },

    weightValidationRules() {
      return this.rentProductRequirmentUtil.isWeightRequired()
        ? "required|between:10,200"
        : "between:10,200";
    },

    shoeSizeValidationRules() {
      return this.rentProductRequirmentUtil.isShoeSizeRequired()
        ? "required"
        : "";
    },

    rentAbilityLevelValidationRules() {
      return this.rentProductRequirmentUtil.isRentAbilityLevelRequired()
        ? "required"
        : "";
    },

    nameDisplayed() {
      return this.rentProductRequirmentUtil.isNameDisplayed();
    },

    ageDisplayed() {
      return this.rentProductRequirmentUtil.isAgeDisplayed();
    },

    sexDisplayed() {
      return this.rentProductRequirmentUtil.isSexDisplayed();
    },

    heightDisplayed() {
      return this.rentProductRequirmentUtil.isHeightDisplayed();
    },

    weightDisplayed() {
      return this.rentProductRequirmentUtil.isWeightDisplayed();
    },

    shoeSizeDisplayed() {
      return this.rentProductRequirmentUtil.isShoeSizeDisplayed();
    },

    rentAbilityLevelDisplayed() {
      return this.rentProductRequirmentUtil.isRentAbilityLevelDisplayed();
    }
  },

  methods: {
    submit() {
      this.$refs.observer.validate();
    },

    doSubmit() {
      this.$emit("change", this.subjectProfileLocal);
    }
  }
};
</script>
